import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import {
  HEDGED_COLOR_HEX,
  UNHEDGED_COLOR_HEX,
  VENUE_WARN_COLOR_HEX
} from './descalceColors';
import { buildDescalceDeepLink } from './descalceDeepLink';
import type { OpenerDescalceRow } from '../../api/useOpenerDescalce';

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const NEUTRAL_COLOR = 'grey.500';

/**
 * `Descalce` column cell — the sort key. Grey `—` when the row's account is
 * unreadable or the market is claimed by more than one rule (`ambiguous`, an
 * account-wide read that belongs to no single row); amber when `resolving`
 * (a settled leg awaiting redemption, not a naked leg); red beyond the
 * `IMBALANCE_TOLERANCE`; green otherwise.
 */
export const DescalceCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.venue) {
    return (
      <Box component="span" data-descalce-state="unavailable" sx={{ color: NEUTRAL_COLOR }}>
        —
      </Box>
    );
  }
  if (row.venue.ambiguous) {
    return (
      <Tooltip
        title="More than one rule claims this market — this venue read is account-wide and belongs to no single row."
        placement="top"
      >
        <Box
          component="span"
          data-descalce-state="ambiguous"
          sx={{ color: NEUTRAL_COLOR, borderBottom: `1px dotted ${NEUTRAL_COLOR}` }}
        >
          —
        </Box>
      </Tooltip>
    );
  }
  const value = row.venue.descalce;
  const resolving = row.venue.resolving;
  const incomplete = row.venue.marketsIncomplete === true;
  const color = resolving
    ? VENUE_WARN_COLOR_HEX
    : value > IMBALANCE_TOLERANCE
      ? UNHEDGED_COLOR_HEX
      : HEDGED_COLOR_HEX;
  const title = resolving
    ? 'RESOLVING: a leg already settled at the venue — a resolution artefact pending redemption, not a naked leg.'
    : incomplete
      ? 'INCOMPLETE MARKET: this rule is missing a ticker or a Polymarket leg — the count above comes from one venue only, not a true cross-venue descalce.'
      : 'Leg imbalance in contracts, account-wide for this market.';
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-descalce-state={resolving ? 'resolving' : 'naked'}
        data-descalce-incomplete={incomplete ? 'true' : undefined}
        sx={{
          color,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          borderBottom: incomplete ? `1px dotted ${color}` : undefined
        }}
      >
        {fmt(value)}
      </Box>
    </Tooltip>
  );
};

/** `YES +N` / `NO +N` from the sign of `venue.signedNet`. */
export const SideCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.venue || row.venue.ambiguous) {
    return (
      <Typography component="span" sx={{ fontSize: '0.75rem', color: NEUTRAL_COLOR }}>
        —
      </Typography>
    );
  }
  const net = row.venue.signedNet;
  if (net === 0) {
    return (
      <Typography component="span" sx={{ fontSize: '0.75rem', color: NEUTRAL_COLOR }}>
        flat
      </Typography>
    );
  }
  const label = net > 0 ? `YES +${fmt(net)}` : `NO +${fmt(Math.abs(net))}`;
  return <Typography component="span" sx={{ fontSize: '0.75rem' }}>{label}</Typography>;
};

/**
 * Chip label/color for a row's `group.podsDesired`. `panicked` is kept
 * distinct from a benign `off` — mirrors the three-way mapping in
 * `entry/groupPodStatus.ts` (`GROUP_POD_STATUS_LABELS` /
 * `GROUP_POD_STATUS_CHIP_COLORS`), since a hard-panicked group is
 * plausibly the very cause of the naked leg this screen exists to surface
 * and must not read the same as a quietly-paused one.
 */
const GROUP_PODS_CHIP: Record<'on' | 'off' | 'panicked', { label: string; color: 'success' | 'default' | 'error' }> = {
  on: { label: 'ON', color: 'success' },
  off: { label: 'OFF', color: 'default' },
  panicked: { label: 'PANICKED', color: 'error' }
};

/** Group name + `ON`/`OFF`/`PANICKED` pod chip + `TEST` chip. A stopped
 *  group's naked leg must still be listed — this cell only marks it, never
 *  hides it. */
export const GroupCell = ({ group }: { group: OpenerDescalceRow['group'] }) => {
  const podsChip = GROUP_PODS_CHIP[group.podsDesired ?? 'off'] ?? GROUP_PODS_CHIP.off;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      <Typography sx={{ fontSize: '0.78rem' }}>{group.name ?? 'Ungrouped'}</Typography>
      {group.id && (
        <Chip
          label={podsChip.label}
          size="small"
          color={podsChip.color}
          data-group-pods={group.podsDesired ?? 'unknown'}
          sx={{ fontSize: '0.6rem', height: 16 }}
        />
      )}
      {group.testAccount && (
        <Chip
          label="TEST"
          size="small"
          color="warning"
          sx={{ fontSize: '0.6rem', height: 16, fontWeight: 700 }}
        />
      )}
    </Stack>
  );
};

/** Poly / Kalshi per-side contract count cell — plain number or `—`. */
export const VenueCountCell = ({ value }: { value: number | null | undefined }) => (
  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem' }}>
    {value == null ? '—' : fmt(value)}
  </Box>
);

export const BeliefCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.belief) {
    return (
      <Tooltip title="No BeliefSnapshot for this rule yet (engine off / restarted).">
        <Box component="span" sx={{ color: NEUTRAL_COLOR }}>—</Box>
      </Tooltip>
    );
  }
  return (
    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem' }}>
      {fmt(row.belief.descalce)}
    </Box>
  );
};

/** `descalce − belief.descalce`, marked when `|diff| > IMBALANCE_TOLERANCE`. */
export const DiffCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.divergence) {
    return <Box component="span" sx={{ color: NEUTRAL_COLOR }}>—</Box>;
  }
  const diff = row.divergence.total;
  const flagged = Math.abs(diff) > IMBALANCE_TOLERANCE;
  const title = flagged
    ? 'The engine belief disagrees with the venue read — possible fill the engine never booked.'
    : 'Venue descalce and engine belief agree within tolerance.';
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-diff-flagged={flagged}
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: flagged ? 700 : 400,
          color: flagged ? UNHEDGED_COLOR_HEX : 'inherit'
        }}
      >
        {diff > 0 ? '+' : ''}
        {fmt(diff)}
      </Box>
    </Tooltip>
  );
};

/** Rule status chip + `hedgeCheckEnabled` shield. */
export const RuleCell = ({ row }: { row: OpenerDescalceRow }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Chip
      label={row.ruleStatus}
      size="small"
      color={row.ruleStatus === 'active' ? 'success' : 'default'}
      sx={{ fontSize: '0.65rem', height: 18, textTransform: 'capitalize' }}
    />
    {row.hedgeCheckEnabled && (
      <Tooltip
        title={`hedge-watchdog enabled${
          row.maxUnhedgedContracts != null
            ? ` — max ${row.maxUnhedgedContracts} unhedged contracts`
            : ''
        }`}
      >
        <ShieldOutlinedIcon sx={{ fontSize: '0.9rem', color: HEDGED_COLOR_HEX }} />
      </Tooltip>
    )}
  </Stack>
);

/** `Go` deep-link to `/predictions/opening?group={id}&subgroup={sid}`,
 *  opened in a new tab. Disabled with a tooltip when the row is ungrouped. */
export const GoCell = ({ row }: { row: OpenerDescalceRow }) => {
  const href = buildDescalceDeepLink(row);
  if (!href) {
    return (
      <Tooltip title="Ungrouped rule (core engine) — no group page to open.">
        <span>
          <IconButton size="small" disabled data-go-disabled="true">
            <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Open this group / subgroup in the opener, in a new tab.">
      <IconButton
        size="small"
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-go-href={href}
      >
        <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
      </IconButton>
    </Tooltip>
  );
};
